import type { SentryEnvironment } from './sentry.types';

export const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;
export const SENTRY_ENVIRONMENT = (process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ||
  'dev-eu-north-1') as SentryEnvironment;

export const SHOW_SENTRY_ERROR_MODAL =
  SENTRY_ENVIRONMENT === 'prod-eu-central-1';

/**
 * 📊 Sentry sample rate configuration by environment
 *
 * Keys match Sentry's API for easy spreading into config:
 * @param tracesSampleRate - % of requests to capture for performance monitoring
 * @param profileSessionSampleRate - % of sessions to profile (replaces deprecated profilesSampleRate)
 * @param replaysSessionSampleRate - % of sessions to replay (conservative: 5-10%)
 * @param replaysOnErrorSampleRate - % of sessions to replay when error occurs (1.0 = always)
 */
const SENTRY_SAMPLE_RATES = {
  'prod-eu-central-1': {
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  },
  'stg-eu-central-1': {
    tracesSampleRate: 0.05,
    profilesSampleRate: 0.05,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
  },
  'dev-eu-north-1': {
    tracesSampleRate: 0.0,
    profilesSampleRate: 0.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
  },
} as const;

// Get config for current environment, fallback to development
const currentConfig =
  SENTRY_SAMPLE_RATES[SENTRY_ENVIRONMENT] ||
  SENTRY_SAMPLE_RATES['dev-eu-north-1'];

/**
 * 🎯 Runtime-specific configs
 * Each runtime has different requirements, so we export tailored configs
 */

// For Node.js runtime (uses profileSessionSampleRate instead of profilesSampleRate)
export const SENTRY_NODE_CONFIG = {
  tracesSampleRate: currentConfig.tracesSampleRate,
  profileSessionSampleRate: currentConfig.profilesSampleRate,
} as const;

// For Edge runtime (no profiling or replays)
export const SENTRY_EDGE_CONFIG = {
  tracesSampleRate: currentConfig.tracesSampleRate,
} as const;

// For Client runtime (has all features)
export const SENTRY_CLIENT_CONFIG = currentConfig;

// 🔇 Ignored Errors (noise reduction)
export const IGNORED_ERRORS = [
  // 🚫 Expected API errors that are handled gracefully by Server Actions
  // Note: 400/409/422 are now filtered via REPORT_TO_SENTRY static property on error classes
  'You need to be logged in to access this page or resource', // 401
  "You don't have permission to access this page or resource", // 403
  "The resource you're looking for can't be found", // 404
  'Your input is invalid', // Client-side validation errors

  // 🚫 Cancelled/Aborted requests (user navigation or timeout)
  'cancelled',
  'canceled',
  'terminated',
  /AbortError/,
  /The user aborted a request/,

  // 🚫 Network errors - chunk loading failures (user connectivity issues)
  'ChunkLoadError',
  'Loading chunk',
  'other side closed',
  /Failed to fetch dynamically imported module/,
  /SocketError/,

  // 🚫 Crypto operation errors (Node.js internal crypto failures)
  'The operation failed for an operation-specific reason',
  /OperationError/,
];

// 📋 Breadcrumb Categories
export const BREADCRUMB_CATEGORIES = {
  AUTH: 'auth',
  API: 'api',
  LIVEBLOCKS: 'liveblocks',
  NAVIGATION: 'navigation',
  UI: 'ui',
  DATA: 'data',
} as const;

// 📊 Breadcrumb Levels
export const BREADCRUMB_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  FATAL: 'fatal',
} as const;

// 🚨 Exception Levels
export const EXCEPTION_LEVELS = {
  WARNING: 'warning',
  ERROR: 'error',
  FATAL: 'fatal',
} as const;
